import { FC, useCallback, useMemo, useRef } from 'react';
import { SelectChangeEvent, Theme, Box } from '@mui/material';
import ModalDialog from '../../../common/components/ModalDialog';
import CreateJiraTicketContent from '../components/CreateJiraTicketContent/CreateJiraTicketContent';
import { makeStyles } from 'tss-react/mui';
import useJiraStore, {
  calcValidationObject,
  createJiraTicket,
  getDependentByProjectFields,
  resetDependentByProjectFields,
  searchAssignee,
  searchComponents,
  searchEpics,
  searchProjects,
  searchReporter,
  setJiraTicket,
  setValidationObject,
} from '../../../jira/api/jira-api';
import { IssueDetails } from '../types/issues-types';
import AppSpinner from '../../../common/components/AppSpinner';
import CreateJiraTicketFooter from '../components/CreateJiraTicketContent/CreateJiraTicketFooter';
import { addJiraTicketToSelectedIssue } from '../../active-issues/store-actions/issue-store-actions';
import { useSnapshot } from 'valtio';
import IssuesExclusionsStore from '../stores/issues-exclusions-store';
import {
  IssueTypeField,
  JiraComponent,
  JiraEpic,
  JiraUser,
  Nullable,
} from 'common-types';
import { debounce, DebouncedFunc } from 'lodash';
import config from '../config/aggregations-columns.json';
import { updateJiraTicketsLatestStatus } from '../../active-issues/actions/active-issues-actions';

const searchApi = {
  project: searchProjects,
  assignee: searchAssignee,
  reporter: searchReporter,
  components: searchComponents,
  epicLink: searchEpics,
} as const;

const CreateJiraTicketModalContainer: FC<
  React.PropsWithChildren<CreateJiraTicketModalContainerProps>
> = ({ open, onClose, selectedIssue }) => {
  const cancelSearch = useRef<DebouncedFunc<() => void>>();
  const { classes } = useStyles();
  const {
    jiraTicket,
    loading,
    loadingProjects,
    loadingAssignees,
    loadingReporters,
    loadingComponents,
    loadingEpics,
    loadingDependentByProjectFields,
    isLoadingSelectableTicketFields,
    projects,
    fields,
    fieldsMetadata,
    dynamicTypeFieldsMetadata,
    validationObject,
    dependentByProjectFields,
  } = useJiraStore();
  const { priorities = [], labels = [] } = fields || {};
  const {
    assigness = null,
    reporters = null,
    issueTypes = null,
    components = null,
    epics = null,
  } = dependentByProjectFields || {};
  const { selectedAggs } = useSnapshot(IssuesExclusionsStore);
  //console.log(fieldsMetadata);
  const selectedAggregationsProps = useMemo(
    () =>
      selectedAggs.length === 0
        ? {
            info: `${selectedIssue.aggregations.items.length} selected`,
            config: config[selectedIssue.aggregations.type],
            items: selectedIssue.aggregations.items,
          }
        : {
            info: `${selectedAggs.length} selected`,
            config: config[selectedIssue.aggregations.type],
            items: selectedAggs,
          },
    [
      selectedAggs,
      selectedIssue.aggregations.items,
      selectedIssue.aggregations.type,
    ],
  );

  const onSubmit = useCallback(async () => {
    const isValid = calcValidationObject();
    if (!isValid) return;

    const jiraTicketRef = await createJiraTicket();

    if (jiraTicketRef?.data) {
      const { data } = jiraTicketRef;
      addJiraTicketToSelectedIssue({
        ticketId: data.id,
        key: data.key,
        link: data.self,
      });
      updateJiraTicketsLatestStatus();
      onClose();
    }
  }, [onClose]);

  const onInputChange = useCallback((e, newInputValue) => {
    const name: Nullable<string> = e ? e.target.name : null;
    cancelSearch.current && cancelSearch.current.cancel();
    cancelSearch.current = debounce(() => {
      name && searchApi[name](newInputValue);
    }, 200);
    cancelSearch.current();
  }, []);

  const onProjectChange = useCallback(
    async (_e, newValue) => {
      setValidationObject(null);
      resetDependentByProjectFields();
      setJiraTicket({
        ...jiraTicket,
        project: newValue,
        issueType: null,
        components: null,
        epicLink: null,
        dynamicFields: null,
      });

      const issueTypes =
        newValue && (await getDependentByProjectFields(newValue.key));
      newValue &&
        setJiraTicket({
          ...jiraTicket,
          project: newValue,
          components: null,
          epicLink: null,
          dynamicFields: null,
          issueType:
            issueTypes?.find(it =>
              it.name.toLocaleLowerCase().includes('bug'),
            ) || null,
        });
    },
    [jiraTicket],
  );

  const onIssueTypeChange = useCallback(
    (_e, newValue) => {
      setValidationObject(null);
      let newValueFieldsMetadata: Nullable<{
        [key: string]: IssueTypeField;
      }> = null;

      if (newValue) {
        newValueFieldsMetadata =
          newValue.fields?.reduce((acc, curr) => {
            acc[curr.key] = curr;
            return acc;
          }, {}) || null;
      }

      setJiraTicket({
        ...jiraTicket,
        issueType: newValue,
        assignee: newValueFieldsMetadata?.['assignee']
          ? jiraTicket.assignee
          : null,
        reporter: newValueFieldsMetadata?.['reporter']
          ? jiraTicket.reporter
          : null,
        epicLink:
          newValueFieldsMetadata &&
          Object.values(newValueFieldsMetadata).find(f =>
            f.name.toLocaleLowerCase().includes('epic'),
          )
            ? jiraTicket.epicLink
            : null,
        priority: newValueFieldsMetadata?.['priority']
          ? jiraTicket.priority
          : null,
        components: newValueFieldsMetadata?.['components']
          ? jiraTicket.components
          : null,
        dynamicFields: null,
      });
    },
    [jiraTicket],
  );

  const onJiraTicketFormChange = useCallback(
    (
      e:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent<string>,
    ) => {
      setValidationObject(null);
      if (['summary', 'comment', 'priority'].includes(e.target.name)) {
        setJiraTicket({
          ...jiraTicket,
          [e.target.name]: e.target.value,
        });
        return;
      }
      if (jiraTicket.description) {
        setJiraTicket({
          ...jiraTicket,
          description: {
            ...jiraTicket.description,
            [e.target.name]: e.target.value,
          },
        });
      }
    },
    [jiraTicket],
  );

  const onDynamicFieldChange = useCallback(
    (
      e: SelectChangeEvent<{
        value: string;
        id: string;
      }>,
    ) => {
      setValidationObject(null);
      setJiraTicket({
        ...jiraTicket,
        dynamicFields: {
          ...jiraTicket.dynamicFields,
          [e.target.name]: e.target.value as {
            value: string;
            id: string;
          },
        },
      });
    },
    [jiraTicket],
  );

  const onFieldChange = useCallback(
    (fieldName: string) => {
      return (
        _e: React.SyntheticEvent<Element, Event>,
        value:
          | Nullable<JiraComponent[]>
          | Nullable<JiraUser>
          | Nullable<string[]>
          | Nullable<JiraEpic>,
      ) => {
        setValidationObject(null);
        setJiraTicket({
          ...jiraTicket,
          [fieldName]: value,
        });
      };
    },
    [jiraTicket],
  );

  return (
    <ModalDialog
      closeAfterTransition
      closeOnClickOutSide={false}
      sx={{
        '& .MuiPaper-root': {
          minWidth: '60vw',
          minHeight: '65vh',
        },
      }}
      modalFooter={
        <CreateJiraTicketFooter
          loadingDialog={
            loadingDependentByProjectFields || isLoadingSelectableTicketFields
          }
          disableSubmit={!!validationObject}
          onClose={onClose}
          onSubmit={onSubmit}
          loadingSubmit={loading}
          classNames={[classes.row]}
        />
      }
      onClose={onClose}
      modalHeader='Create Jira Issue'
      open={open}>
      <Box justifyContent='flex-start' flexDirection='column' display='flex'>
        {!isLoadingSelectableTicketFields && jiraTicket.description ? (
          <CreateJiraTicketContent
            validationObject={validationObject}
            fieldsMetadata={fieldsMetadata}
            dynamicTypeFieldsMetadata={dynamicTypeFieldsMetadata}
            loadingDependentByProjectFields={loadingDependentByProjectFields}
            loadingProjects={loadingProjects}
            loadingAssignees={loadingAssignees}
            loadingReporters={loadingReporters}
            loadingComponents={loadingComponents}
            loadingEpics={loadingEpics}
            onIssueTypeChange={onIssueTypeChange}
            onInputChange={onInputChange}
            onProjectChange={onProjectChange}
            selectedAggregationsProps={selectedAggregationsProps}
            onJiraTicketFormChange={onJiraTicketFormChange}
            onDynamicFieldChange={onDynamicFieldChange}
            onFieldChange={onFieldChange}
            jiraTicketFields={jiraTicket}
            projects={projects}
            issueTypes={issueTypes}
            reporters={reporters}
            assigness={assigness}
            components={components}
            epics={epics}
            prioritis={priorities}
            labels={labels}
          />
        ) : (
          <AppSpinner />
        )}
      </Box>
    </ModalDialog>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  row: {
    padding: theme.spacing(1),
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
  },
}));

export interface CreateJiraTicketModalContainerProps {
  open: boolean;
  onClose: () => void;
  selectedIssue: IssueDetails;
}

export default CreateJiraTicketModalContainer;
